"use strict";
/**
 * 前端API客户端基类
 * 内部直接使用 @fegobull/webapi 的 useRequest，提供统一的API调用封装
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.BaseApiClient = exports.ApiError = void 0;
exports.isApiError = isApiError;
exports.isApiServerError = isApiServerError;
exports.isApiBusinessError = isApiBusinessError;
exports.isApiNetworkError = isApiNetworkError;
exports.isApiRetryable = isApiRetryable;
const webapi_1 = require("@fegobull/webapi");
const site_location_1 = require("./site-location");
// ============ API 错误类 ============
/**
 * API 错误类
 *
 * 包含 HTTP 状态码和业务错误码，便于区分错误类型：
 * - 服务器错误（HTTP 5xx）：可重试
 * - 网络错误（超时、断网）：可重试
 * - 业务错误（HTTP 200, code !== 0）：需展示给用户
 */
class ApiError extends Error {
    constructor(options) {
        super(`API Error: HTTP ${options.httpStatus}, Code ${options.businessCode}`);
        this.name = 'ApiError';
        this.httpStatus = options.httpStatus;
        this.businessCode = options.businessCode;
        this.data = options.data;
        this.rawResponse = options.rawResponse;
    }
    /** 是否为服务器错误（HTTP 5xx） */
    get isServerError() {
        return this.httpStatus >= 500 && this.httpStatus < 600;
    }
    /** 是否为业务错误（HTTP 200, code !== 0） */
    get isBusinessError() {
        return this.httpStatus === 200 && this.businessCode !== 0;
    }
    /** 是否为网络错误（状态码为 0，通常是超时或断网） */
    get isNetworkError() {
        return this.httpStatus === 0;
    }
    /** 是否为可重试错误（服务器错误 或 网络错误） */
    get isRetryable() {
        return this.isServerError || this.isNetworkError;
    }
}
exports.ApiError = ApiError;
// ============ 类型守卫 ============
/** 判断是否为 ApiError */
function isApiError(e) {
    return e instanceof ApiError;
}
/** 判断是否为服务器错误（HTTP 5xx），可重试 */
function isApiServerError(e) {
    return isApiError(e) && e.isServerError;
}
/** 判断是否为业务错误（HTTP 200, code !== 0），需展示 */
function isApiBusinessError(e) {
    return isApiError(e) && e.isBusinessError;
}
/** 判断是否为网络错误（超时、断网等），可重试 */
function isApiNetworkError(e) {
    return isApiError(e) && e.isNetworkError;
}
/** 判断是否为可重试错误（服务器错误 或 网络错误） */
function isApiRetryable(e) {
    return isApiError(e) && e.isRetryable;
}
// ============ API 客户端基类 ============
/**
 * 前端API客户端基类
 *
 * 使用方式：
 * - 成功时直接返回 data（类型为 T）
 * - 失败时抛出 ApiError，包含 httpStatus、businessCode、data
 *
 * 错误处理示例：
 * ```typescript
 * try {
 *   const list = await api.getList();  // list: ListData
 * } catch (e) {
 *   if (isApiBusinessError(e)) {
 *     showToast(`业务错误: ${e.businessCode}`);
 *   }
 * }
 * ```
 *
 * 重试示例：
 * ```typescript
 * @Retry({ when: isApiServerError, maxAttempts: 3 })
 * async getList() { return this.get<ListData>('list'); }
 * ```
 */
class BaseApiClient {
    constructor(options = {}) {
        const { get, post } = webapi_1.use.useRequest();
        this.requestMethods = { get, post };
        this.baseURL = `${BaseApiClient.ARK_WEB_API_PREFIX}/${options.apiPrefix}`;
    }
    buildURL(path) {
        return `${this.baseURL}/${path}`;
    }
    /**
     * 将站点地区 header 合并到请求 options 中，用于网关按地区转发。
     *
     * 站点地区由 getSiteLocation() 从 Fego 注入的 siteId 解析，取不到会抛错。
     * 注意：Fego 的 xhr 封装使用 `header`（单数）作为 key。
     */
    mergeOptions(options) {
        return {
            ...options,
            header: {
                [site_location_1.SITE_LOCATION_HEADER]: (0, site_location_1.getSiteLocation)(),
                ...options?.header,
            },
        };
    }
    /**
     * 安全解析 JSON 字符串
     * TODO ：： 抽离到 utils 库，或用开源库代替
     */
    safeParseJSON(text) {
        if (!text)
            return undefined;
        try {
            return JSON.parse(text);
        }
        catch {
            return undefined;
        }
    }
    /**
     * 将 useRequest 的错误标准化为 ApiError。
     *
     * Fego useRequest 对 code !== 0 的响应会 reject，但抛出的对象形态不固定：
     * - 网络错误/超时：抛 ProgressEvent，.target 指向 XMLHttpRequest
     * - 业务错误(code !== 0)：可能直接抛响应体 {code, data, ...}，无 .target
     * 两条路径都需要正确提取 httpStatus 和 businessCode。
     */
    normalizeError(error) {
        if (error &&
            typeof error === 'object' &&
            'code' in error &&
            'message' in error) {
            return new ApiError({
                httpStatus: 200,
                businessCode: error.code,
                data: error.message,
                rawResponse: error,
            });
        }
        const xhr = error?.target;
        const httpStatus = xhr?.status ?? 0;
        const data = this.safeParseJSON(xhr?.responseText);
        // 优先从 XHR responseText 解析；fallback 检查 error 自身是否为响应体
        const businessCode = data?.code ?? error?.code ?? -1;
        const resolvedData = data ?? (typeof error === 'object' ? error : undefined);
        // Fego 业务错实际是 HTTP 200，但 error.target 缺失导致 httpStatus 为 0
        const resolvedHttpStatus = httpStatus === 0 && typeof businessCode === 'number' && businessCode > 0
            ? 200
            : httpStatus;
        return new ApiError({
            httpStatus: resolvedHttpStatus,
            businessCode,
            data: resolvedData,
            rawResponse: error,
        });
    }
    async get(path, params) {
        const url = this.buildURL(path);
        try {
            const data = await this.requestMethods.get(url, params, this.mergeOptions());
            return data;
        }
        catch (error) {
            throw this.normalizeError(error);
        }
    }
    async post(path, data, options) {
        const url = this.buildURL(path);
        try {
            const result = await this.requestMethods.post(url, data, this.mergeOptions(options));
            return result;
        }
        catch (error) {
            throw this.normalizeError(error);
        }
    }
}
exports.BaseApiClient = BaseApiClient;
BaseApiClient.ARK_WEB_API_PREFIX = '/apiv2/activity';
